/* eslint-disable react-hooks/exhaustive-deps */
import {formatSecondsToMinSec, handleError, openLinkInNewTab} from "helpers"
import {KlassappTableHOC} from "HOC"
import {useModel, useMutation} from "hooks"
import {useCallback, useEffect} from "react"
import {studentCallLogService} from "services"
import {KlassappTableProps} from "types/common"
import {BaseDepartmentId} from "types/departments"
import {FilterKey, FilterList} from "types/filter"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {ReactComponent as PlayIcon} from "./assets/play.svg"
import {StudentCallLog} from "types/student-call-log"
import moment from "moment"

type Props = KlassappTableProps & {
    filterKey?: FilterKey
    filterParams?: {
        filters: FilterList
        search?: string
    }
    departmentId?: BaseDepartmentId
    studentProfileId?: number
}

function StudentCallLogList(props: Props) {
    const model = useModel()
    const {
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        allFields,
        isLoading,
        orderField,
        isLoadedTableFuncs,
        dispatch,
        dispatchFunc
    } = props
    const {filterKey, filterParams, departmentId} = props

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getFilterMemoryKey", func: getFilterKey},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, page, pageSize, orderField?.field, orderField?.order, filterParams])

    const getFilterKey = useCallback(() => {
        return filterKey
    }, [filterKey])

    const getData = useCallback(async () => {
        try {
            dispatch({isLoading: true})

            const storageData = model.getStorageFilter(filterKey)
            let orderFieldData = orderField
            if (storageData?.colOrd) {
                const columns: any[] = getColumns()
                const columnOrder = columns.find((column: any) => column.columnIndex === storageData.colOrd.idx)
                orderFieldData = {field: columnOrder?.orderField || columnOrder?.field, order: storageData.colOrd.order}
            }

            const payload = {
                range: {
                    pageSize: pageSize,
                    page: page
                },
                departmentId,
                ...filterParams,
                sort: orderFieldData?.field
                    ? {
                          orderBy: orderFieldData.field,
                          orderDir: orderFieldData.order
                      }
                    : undefined
            }
            const {data, total} = await studentCallLogService.list(payload)
            const formattedDocuments = data.map((item: StudentCallLog) => {
                return {
                    ...item,
                    date: moment.utc(item.createdAt).local().format(model.getUserDateFormat()),
                    time: moment.utc(item.createdAt).local().format(model.getUserTimeFormat()),
                    callLength: formatSecondsToMinSec(item.duration),
                    recording: renderRecording(item),
                    isInbound: item.isInbound ? "Inbound" : "Outbound"
                }
            })
            dispatch({data: formattedDocuments, total})
        } catch (err) {
            handleError(err)
        } finally {
            dispatch({isLoading: false})
        }
    }, [filterKey, filterParams, page, pageSize, orderField])

    const renderRecording = (item: StudentCallLog) => {
        if (item.recordingUrl) {
            return (
                <a href={item.recordingUrl} target="_blank" rel="noreferrer">
                    <PlayIcon />
                </a>
            )
        }
        return null
    }

    const getFields = () => {
        const fields = [
            "Date",
            "Time",
            "Student ID",
            "Student Name",
            "Phone",
            "Staff Name",
            "Inbound/Outbound",
            "Call Length",
            "Recording"
        ]
        return fields
    }

    const getColumns = () => {
        const cols: any = [
            {
                title: "Date",
                field: "date",
                columnIndex: 1
            },
            {
                title: "Time",
                field: "time",
                columnIndex: 2
            },
            {
                title: "Student ID",
                field: "customProfileId",
                columnIndex: 3
            },
            {
                title: "Student Name",
                field: "studentFullName",
                columnIndex: 4
            },
            {
                title: "Phone",
                field: "phone",
                columnIndex: 5
            },
            {
                title: "Staff Name",
                field: "staffFullName",
                columnIndex: 6
            },
            {
                title: "Inbound/Outbound",
                field: "isInbound",
                columnIndex: 7
            },
            {
                title: "Call Length",
                field: "callLength",
                columnIndex: 8
            },
            {
                title: "Recording",
                field: "recording",
                columnIndex: 9
            }
        ]

        return cols
    }

    return (
        <>
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                orderField={orderField}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
            />
        </>
    )
}

export const StudentCallLogListView = KlassappTableHOC(StudentCallLogList)
